import { Injectable } from '@angular/core';
import {ApiService} from "./api.service";
import {UserService} from "./user.service";
import {PopupService} from "./popup.service";
import {Observable, throwError} from "rxjs";
import {catchError, map, switchMap} from "rxjs/operators";
import {environment} from "../../environments/environment";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {AuthService} from "./auth.service";
import {select, Store} from "@ngrx/store";
import {selectAuthUser, selectToken} from "../state/auth/auth.selectors";
import {selectReport} from "../state/search/search.selectors";
import { selectSearchStateHubspotChanges } from '../state/hubspot/hubspot.selectors';
import {FuzzySearchParams} from "../state/search/search.actions";

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  private apiUrl = environment.api.url;

  token: string;

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private userService: UserService,
    private popupService: PopupService,
    private authService: AuthService,
    private store: Store<any>
  ) { }


  all() {
    // return this.apiService.doRequest('api/search/all', 'GET', '');
    return this.apiService.doRequest('api/v1/search/all', 'GET', '');
  }

  find(searchString){
    return this.apiService.doRequest('api/search/find', 'POST', {search: searchString});
  }

  getNotes(){
    return this.apiService.doRequest('api/notes','GET' , '');
  }

  getAssistantHistory(search_id: number, search_company_id: number){
    return this.apiService.doRequest('api/v1/get_tag_response','POST' , { search_id, search_company_id, tag: 'ASSISTANT' });
  }

  askAssistant(prompt: string, data: any, search_id: number, search_company_id: number){
    return this.apiService.doRequest('api/v1/ask_assistant', 'POST', { prompt, data, search_id, search_company_id });
  }

  /*
  newSearch(data){
    return this.apiService.doRequest('api/search/new', 'POST', data);
  }
  */

  newSearchOld(data){
    return this.apiService.doRequest('api/search/new', 'POST', data);
  }

  testJob(data) {
    return this.apiService.doRequest('api/search/research-company', 'POST', data);
  }

  get(id){
    // return this.apiService.doRequest('api/search/' + id, 'GET', '');
    return this.apiService.doRequest('api/v1/search/' + id, 'GET', '');
  }

  shareReport(data){
    return this.apiService.doRequest('api/sharing/invite', 'POST', data);
  }

  allSearches(
    page: number,
    perPage: number,
    search: string,
    tags: any,
    archived: boolean,
    workspaceId?: string,  // Add as optional parameter
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    dateRange?: string
  ): Observable<{searches: []}> {
    let url = `${this.apiUrl}/search/all`;

    const data: any = {
      page: page,
      per_page: perPage,
      search: search,
      tags: tags,
      archived: archived,
    }

    // Only add workspaceId to the data object if it's provided
    if (workspaceId) {
      data.workspace_id = workspaceId;
    }

    if (sortBy) {
      data.sort_by = sortBy;
    }
    if (sortDir) {
      data.sort_dir = sortDir;
    }
    if (dateRange && dateRange !== 'all') {
      data.date_range = dateRange;
    }

    this.authService.token().subscribe(token => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      })
    };

    return this.http.post<any>(url, data, httpOptions).pipe(
      map(response => {
        if (!response.data || !response.data.searches) {
          throw new Error('All searches failed: No searches received');
        }
        return {
          searches: response.data.searches
        }
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  newSearch(user_id: number, linkedin_profile: string, workspace_id?: string): Observable<{search: any}> {
    const url = `${this.apiUrl}/search/new`;

    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` // Use the token directly here
          })
        };

        // Create the data object
        const data: any = { user_id, linkedin_profile };

        // Only add workspace_id if it's provided
        if (workspace_id) {
          data.workspace_id = workspace_id;
        }

        return this.http.post<any>(url, data, httpOptions);
      }),
      map(response => {
        if (!response.data) {
          throw new Error('New search failed: No search received');
        }
        return { search: response.data };
      }),
      catchError(error => throwError(() => error)) // Propagate the error
    );
  }

  fetchReport(reportId: number): Observable<{ report: any }> {
    let url = `${this.apiUrl}/search/${reportId}`;

    this.authService.token().subscribe(token => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      })
    };

    return this.http.get<any>(url, httpOptions).pipe(
      map(response => {
        if (!response || !response.data.search) {
          throw new Error('Fetch report failed: No report data received');
        }
        return {
          report: response.data.search
        };
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /** Regenerate the core report insights on demand (consumes 1 credit server-side). */
  regenerateReport(reportId) {
    const url = `${this.apiUrl}/search/${reportId}/regenerate`;

    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          })
        };

        return this.http.post<any>(url, {}, httpOptions);
      }),
      catchError(error => throwError(() => error))
    );
  }

  archiveReport(reportId) {
    const url = `${this.apiUrl}/search/${reportId}/archive`;

    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` // Use the token directly here
          })
        };

        return this.http.post<any>(url, { reportId }, httpOptions);
      }),
      map(response => {
        if (!response.data) {
          throw new Error('Archive report failed: No report received');
        }
        return { report: response.data };
      }),
      catchError(error => throwError(() => error)) // Propagate the error
    );
  }

  restoreReport(reportId) {
    const url = `${this.apiUrl}/search/${reportId}/restore`;

    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` // Use the token directly here
          })
        };

        return this.http.post<any>(url, { reportId }, httpOptions);
      }),
      map(response => {
        if (!response.data) {
          throw new Error('Restore report failed: No report received');
        }
        return { report: response.data };
      }),
      catchError(error => throwError(() => error)) // Propagate the error
    );
  }

  fetchSelfReport(public_profile_url): Observable<{ report: any }> {
    let url = `${this.apiUrl}/search/self/${public_profile_url}`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };

    return this.http.get<any>(url, httpOptions).pipe(
        map(response => {
          if (!response || !response.data.search) {
            throw new Error('Fetch self report failed: No report data received');
          }
          return {
            report: response.data.search
          };
        }),
        catchError(error => {
          throw error;
        })
    );
  }

  report(): Observable<any | null> {
    return this.store.pipe(select(selectReport));
  }

  search_state_hubspot_changes(): Observable<any | null> {
    return this.store.pipe(select(selectSearchStateHubspotChanges));
  }

  fuzzySearch(params: FuzzySearchParams): Observable<{ data: any[]; message: string }> {
    const url = `${this.apiUrl}/search/fuzzy`;

    // Fetch the token from AuthService
    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      }),
    };

    // Build the request payload with all parameters
    const data: any = {
      q_term: params.query, // Keep the original parameter name for backward compatibility
    };

    // Only add optional parameters if they exist
    if (params.person_titles && params.person_titles.length > 0) {
      data.person_titles = params.person_titles;
    }

    if (params.person_seniorities && params.person_seniorities.length > 0) {
      data.person_seniorities = params.person_seniorities;
    }

    if (params.person_locations && params.person_locations.length > 0) {
      data.person_locations = params.person_locations;
    }

    if (params.organization_locations && params.organization_locations.length > 0) {
      data.organization_locations = params.organization_locations;
    }

    return this.http.post<any>(url, data, httpOptions).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Fuzzy search failed: ' + response.message);
        }

        return {
          data: response.data,
          message: response.message,
        };
      }),
      catchError((error) => {
        console.error('[fuzzy-search.service.ts] error', error);
        return throwError(error);
      })
    );
  }

  enrichProfile(searchId: number): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/search/${searchId}/insights/enrich`;

    return this.authService.token().pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          })
        };

        // POST request with empty body or any required data
        const data = {}; // Add any required payload here if needed

        return this.http.post<any>(url, data, httpOptions).pipe(
          map((response) => {
            console.log('[report.service.ts] Enrich profile response', response);

            if (!response.data) {
              throw new Error('Profile enrichment failed: ' + response.message);
            }

            return {
              data: response.data,
              message: response.message
            };
          }),
          catchError((error) => {
            console.error('[report.service.ts] Enrich profile error', error);
            return throwError(() => new Error('Failed to enrich profile.'));
          })
        );
      }),
      catchError((error) => {
        console.error('[report.service.ts] Token error', error);
        return throwError(() => new Error('Failed to fetch token.'));
      })
    );
  }

  generateInsights(searchId: number, insightType: string): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/search/${searchId}/insights/${insightType}`;

    return this.authService.token().pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          })
        };

        const data = {
          id: searchId,
          insightType: insightType
        };

        return this.http.post<any>(url, data, httpOptions).pipe(
          map((response) => {
            console.log(`[search.service.ts] Generate insights (${insightType}) response`, response);

            if (!response.data) {
              throw new Error('Insights generation failed: ' + response.message);
            }

            return {
              data: response.data,
              message: response.message || 'Insights generated successfully'
            };
          }),
          catchError((error) => {
            console.error(`[search.service.ts] Generate insights (${insightType}) error`, error);
            return throwError(() => new Error(`Failed to generate ${insightType} insights.`));
          })
        );
      }),
      catchError((error) => {
        console.error('[search.service.ts] Token error', error);
        return throwError(() => new Error('Failed to fetch token.'));
      })
    );
  }

  updateContactInfo(searchId: number, type: string, emails: string[] | null, phones: string[] | null): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/search/contact-info`;

    return this.authService.token().pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          })
        };

        const data: any = {
          search_id: Number(searchId),
          type: type,
        };

        if (emails !== null) {
          data.emails = emails;
        }
        if (phones !== null) {
          data.phones = phones;
        }

        console.log('[search.service.ts] Update contact info request payload', data);

        return this.http.put<any>(url, data, httpOptions).pipe(
          map((response) => {
            console.log('[search.service.ts] Update contact info FULL response', JSON.stringify(response, null, 2));

            if (!response.data) {
              throw new Error('Contact info update failed: ' + (response.metadata?.message || response.message));
            }

            return {
              data: response.data,
              message: response.metadata?.message || response.message || 'Contact info updated successfully'
            };
          }),
          catchError((error) => {
            console.error('[search.service.ts] Update contact info error', error);
            return throwError(() => error);
          })
        );
      }),
      catchError((error) => {
        console.error('[search.service.ts] Token error', error);
        return throwError(() => new Error('Failed to fetch token.'));
      })
    );
  }

  updateRevenue(searchId: number, searchCompanyId: number, revenue: string, longDescription: string): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/insights/update`;

    return this.authService.token().pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          })
        };

        const data = {
          search_id: Number(searchId),
          search_company_id: Number(searchCompanyId),
          assistant_tag_id: 'ASK_ASSISTANT_COMPANY_REVENUE',
          short: revenue,
          long: longDescription
        };

        return this.http.put<any>(url, data, httpOptions).pipe(
          map((response) => {
            console.log('[search.service.ts] Update revenue response', response);

            if (!response.data) {
              throw new Error('Revenue update failed: ' + response.message);
            }

            return {
              data: response.data,
              message: response.message || 'Revenue updated successfully'
            };
          }),
          catchError((error) => {
            console.error('[search.service.ts] Update revenue error', error);
            return throwError(() => new Error('Failed to update revenue.'));
          })
        );
      }),
      catchError((error) => {
        console.error('[search.service.ts] Token error', error);
        return throwError(() => new Error('Failed to fetch token.'));
      })
    );
  }

}
